
import * as THREE from "three";
import type {
  ActiveEffects,
  AssetEffectConfig,
  EffectArchetype,
} from "./types";
import type { MeshEffect } from "./MeshEffect";
import { WindMeshEffect } from "./effects/WindMeshEffect";

// ---------------------------------------------------------------------------
// MeshEffectManager — orchestrates mesh-level effect application and per-frame updates
// ---------------------------------------------------------------------------

/**
 * Singleton-like manager for the mesh-level effect system.
 *
 * Responsibilities:
 *   1. Hold the registry of known mesh effects (WindMeshEffect initially)
 *   2. Resolve archetype + per-asset params → apply the right driver
 *   3. Tick shared uniforms once per frame (time, wind direction/strength)
 *
 * Data flow:
 *   archetype  → from SceneObject.archetype (compiler-emitted)
 *   per-asset  → from ObjectAsset.metadata.effects (cached manifest)
 *   scene-level → from AtmosphereConfig.wind (ActiveEffects)
 *
 * Note: MeshEffectManager handles shader mutations on existing meshes.
 * For additive scene-graph elements (emitters, lights), see ObjectEffectsRenderer.
 */
class MeshEffectManagerImpl {
  private effects: Map<string, MeshEffect<unknown>> = new Map();

  /** Cache: objectId → AssetEffectConfig (effect params from metadata) */
  private assetCache: Map<string, AssetEffectConfig> = new Map();
  private cacheLoaded = false;
  private cacheLoading: Promise<void> | null = null;

  /** Version counter — increments when cache is populated.
   *  React components subscribe to this to re-apply effects after async load. */
  private _cacheVersion = 0;
  private _listeners: Set<() => void> = new Set();

  constructor() {
    // Register built-in mesh effects
    const wind = new WindMeshEffect();
    this.effects.set(wind.kind, wind as MeshEffect<unknown>);
  }

  /** Current cache version. Increment each time the cache is (re)populated. */
  get cacheVersion(): number {
    return this._cacheVersion;
  }

  /** Subscribe to cache version changes. Returns an unsubscribe function. */
  onCacheReady(listener: () => void): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  private notifyCacheReady(): void {
    this._cacheVersion++;
    for (const listener of this._listeners) listener();
  }

  /**
   * Ensure the asset metadata cache is populated.
   * Fetches all models from the API on first call, then caches effect configs.
   * Safe to call multiple times — returns immediately after first load.
   */
  async ensureCacheLoaded(): Promise<void> {
    if (this.cacheLoaded) return;
    if (this.cacheLoading) return this.cacheLoading;

    this.cacheLoading = (async () => {
      try {
        const { api } = await import("../../api");
        let page = 1;
        const pageSize = 100;
        let hasMore = true;

        while (hasMore) {
          const result = await api.listModels({ page, pageSize });
          for (const model of result.items) {
            const effects = model.metadata?.effects;
            if (effects) {
              this.assetCache.set(model.id, effects);
            }
          }
          hasMore = result.pagination.hasNext;
          page++;
        }

        this.cacheLoaded = true;
        this.notifyCacheReady();
      } catch (err) {
        console.warn("[MeshEffectManager] Failed to load asset cache:", err);
      }
    })();

    return this.cacheLoading;
  }

  /**
   * Apply all relevant mesh effects to a cloned THREE.Object3D.
   *
   * Called from GLBModel after scene.clone(), once per object instance.
   *
   * @param scene    The cloned Three.js scene graph for this object
   * @param archetype The object's archetype (from SceneObject.archetype)
   * @param objectId  The object's asset ID (for per-asset param lookup)
   * @param activeEffects Current scene-level effects (from AtmosphereConfig)
   */
  applyEffects(
    scene: THREE.Object3D,
    archetype: EffectArchetype,
    objectId: string,
    activeEffects: ActiveEffects,
  ): void {
    // Look up per-asset effect params from cache
    const assetEffects = this.assetCache.get(objectId);

    for (const [, effect] of this.effects) {
      // Check if this effect supports the archetype
      const driver = effect.resolveDriver(archetype);
      if (!driver) continue;

      // Merge params: asset metadata overrides > effect defaults
      const defaults = effect.getDefaultParams(archetype) as Record<
        string,
        unknown
      >;
      const windParams = assetEffects?.wind as
        | Record<string, unknown>
        | undefined;
      const params = { ...defaults, ...windParams };

      driver.apply(scene, params, activeEffects);
    }
  }

  /**
   * Tick all drivers' shared uniforms.
   * Called once per frame from SceneRenderer's useFrame hook.
   *
   * @param activeEffects Current scene-level effects
   * @param time  Elapsed time in seconds (from R3F clock)
   */
  updateGlobalEffects(activeEffects: ActiveEffects, time: number): void {
    for (const [, effect] of this.effects) {
      // Update all drivers owned by this effect
      for (const archetype of effect.supportedArchetypes) {
        const driver = effect.resolveDriver(archetype);
        if (driver) {
          driver.update(activeEffects, time);
          // Only update each unique driver once per frame
          break;
        }
      }
    }
  }

  /** Reset the asset cache (e.g. when the asset manifest changes). */
  resetCache(): void {
    this.assetCache.clear();
    this.cacheLoaded = false;
    this.cacheLoading = null;
    this._cacheVersion++;
  }

  /** Dispose all drivers and release shader references. */
  dispose(): void {
    for (const [, effect] of this.effects) {
      for (const archetype of effect.supportedArchetypes) {
        const driver = effect.resolveDriver(archetype);
        if (driver) driver.dispose();
      }
    }
    this.resetCache();
  }
}

/** Global singleton MeshEffectManager instance. */
export const meshEffectManager = new MeshEffectManagerImpl();
